import AdminDashboard from "../pages/admin/AdminDashboard";
import SystemUsers from "../pages/admin/SystemUsers";
import Roles from "../pages/admin/Roles";
import AddRole from "../pages/admin/AddRole";
import MembersManagement from "../pages/admin/MembersManagement";
import ClubsManagement from "../pages/admin/ClubsManagement";
import FacilityManagement from "../pages/admin/FacilityManagement";
import FacilityBlocksManagement from "../pages/admin/FacilityBlocksManagement";
import BookingsManagement from "../pages/admin/BookingsManagement";
import PaymentsManagement from "../pages/admin/PaymentsManagement";
import { ShroffFindConfirmPage, ShroffPaymentHistoryPage } from "../pages/admin/ShroffPaymentsTab";
import FeesManagement from "../pages/admin/FeesManagement";
import AttendanceManagement from "../pages/admin/AttendanceManagement";
import IdCardPrinting from "../pages/admin/IdCardPrinting";
import IdCardReprints from "../pages/admin/IdCardReprints";
import MembershipRenewals from "../pages/admin/MembershipRenewals";
import ClubRenewals from "../pages/admin/ClubRenewals";
import LogViewer from "../pages/admin/LogViewer";
import BookingLogs from "../pages/admin/BookingLogs";
import SystemUserLoginLogs from "../pages/admin/SystemUserLoginLogs";
import { ICONS } from "../components/common/DashboardUI";
import { FEATURE_FLAGS } from "./featureFlags";

// Single source of truth for the back-office's sidebar + routing. Both
// AdminLayout (sidebar links) and App.js (nested <Route>s under /admin) map
// over this list instead of hand-declaring each in two places, so a module's
// visibility and its route access are always driven by the same permission
// and can never drift apart.
//
// Each entry is a MODULE, gated by the single "view_*" permission that owns
// it (e.g. "view_facilities"). Action-level permissions (create_*, edit_*,
// delete_*, approve_*, ...) are deliberately NOT modeled here — they never
// produce their own sidebar entry. Each page reads those directly off
// useAuth().can(...) to show/hide its own buttons (see SystemUsers.jsx,
// FacilityManagement.jsx, IdCardPrinting.jsx, Roles.jsx for the pattern).
//
// `permission: null` means "any authenticated back-office user" — reserved
// for the Dashboard landing page, which has no data of its own (it only
// surfaces permission-gated summary cards) and must stay reachable so login
// always has somewhere safe to land, even for a role missing every other
// module permission.
//
// `requireSuperAdmin: true` is for a module gated by the backend on the top
// role itself (middleware('role:admin')) rather than a "view_*" permission —
// there's nothing to pass to can(). No current entry uses it, but ProtectedRoute
// and AdminLayout both still support it for whenever one does.
const ALL_ADMIN_NAV = [
  {
    key: "dashboard",
    path: "", // "" = the /admin index route
    to: "/admin",
    end: true,
    label: "Dashboard",
    icon: ICONS.dashboard,
    permission: null,
    Component: AdminDashboard,
  },
  {
    key: "users",
    path: "users",
    to: "/admin/users",
    end: false,
    label: "System Users",
    icon: ICONS.users,
    permission: "view_system_users",
    Component: SystemUsers,
  },
  {
    key: "roles",
    path: "roles",
    to: "/admin/roles",
    end: false,
    label: "Roles & Permissions",
    icon: ICONS.roles,
    permission: "view_roles",
    Component: Roles,
  },
  {
    key: "add-role",
    path: "add-role",
    to: "/admin/add-role",
    end: false,
    label: "Add Role",
    icon: ICONS.plus,
    permission: "create_roles",
    Component: AddRole,
  },
  {
    key: "members",
    // "/*" — owns its own nested <Routes> (list + per-member detail page).
    path: "members/*",
    to: "/admin/members",
    end: false,
    label: "Members",
    icon: ICONS.user,
    permission: "view_members",
    Component: MembersManagement,
  },
  {
    key: "clubs",
    // "/*" — owns its own nested <Routes> (list + per-club detail page).
    path: "clubs/*",
    to: "/admin/clubs",
    end: false,
    label: "Clubs",
    icon: ICONS.clubs,
    permission: "view_clubs",
    Component: ClubsManagement,
  },
  {
    key: "facilities",
    path: "facilities",
    to: "/admin/facilities",
    end: false,
    label: "Facilities",
    icon: ICONS.facility,
    permission: "view_facilities",
    Component: FacilityManagement,
  },
  {
    key: "facility-blocks",
    path: "facility-blocks",
    to: "/admin/facility-blocks",
    end: false,
    label: "Facility Blocks",
    icon: ICONS.block,
    permission: "view_facility_blocks",
    Component: FacilityBlocksManagement,
  },
  {
    key: "bookings",
    path: "bookings",
    to: "/admin/bookings",
    end: false,
    label: "Bookings",
    icon: ICONS.bookings,
    permission: "view_bookings",
    Component: BookingsManagement,
    featureFlag: "bookingUI",
  },
  {
    // Distinct backend permission from "view_bookings"/"view_booking_details"
    // above — a role needs "view_booking_logs" granted separately to see
    // this audit trail, same as "view_logs" earns log-viewer its own entry.
    key: "booking-logs",
    path: "booking-logs",
    to: "/admin/booking-logs",
    end: false,
    label: "Booking Logs",
    icon: ICONS.clock,
    permission: "view_booking_logs",
    Component: BookingLogs,
    featureFlag: "bookingUI",
  },
  {
    key: "payments",
    path: "payments",
    to: "/admin/payments",
    end: false,
    label: "Payments",
    icon: ICONS.payments,
    permission: "view_payments",
    Component: PaymentsManagement,
  },
  {
    // Shroff counter desk (ShroffPaymentsTab.jsx). GET /api/shroff/payments/search
    // accepts either permission, so this entry takes an "any of" array — that
    // also lets the seeded Shroff role (no view_payments) reach it.
    key: "shroff-find-confirm",
    path: "shroff-payments",
    to: "/admin/shroff-payments",
    end: false,
    label: "Find & Confirm",
    icon: ICONS.search,
    permission: ["search_shroff_payments", "view_payments"],
    Component: ShroffFindConfirmPage,
  },
  {
    // Its own permission (GET /api/shroff/payments/history) — not implied by
    // view_payments, and seeded onto no role by default.
    key: "shroff-payment-history",
    path: "shroff-payment-history",
    to: "/admin/shroff-payment-history",
    end: false,
    label: "Payment History",
    icon: ICONS.clock,
    permission: "view_shroff_payment_history",
    Component: ShroffPaymentHistoryPage,
  },
  {
    key: "fees",
    path: "fees",
    to: "/admin/fees",
    end: false,
    label: "Fees",
    icon: ICONS.fees,
    permission: "view_fees",
    Component: FeesManagement,
  },
  {
    key: "attendance",
    path: "attendance",
    to: "/admin/attendance",
    end: false,
    label: "Attendance",
    icon: ICONS.attendance,
    permission: "view_attendance",
    Component: AttendanceManagement,
  },
  {
    key: "id-cards",
    path: "id-cards",
    to: "/admin/id-cards",
    end: false,
    label: "Print ID Cards",
    icon: ICONS.card,
    permission: "view_id_card_print_requests",
    Component: IdCardPrinting,
  },
  {
    // Gated on the same permission as "Print ID Cards" above rather than a
    // dedicated one — Instructor and Director/Admin both already have
    // "view_id_card_print_requests", and this module's finer actions (submit
    // a request, approve/reject, confirm a shroff payment) are gated per-tab
    // inside the page itself via useAuth().can(...), same pattern as
    // PaymentsManagement's "Manual Approvals" tab. See IdCardReprints.jsx.
    key: "id-card-reprints",
    path: "id-card-reprints",
    to: "/admin/id-card-reprints",
    end: false,
    label: "ID Card Reprints",
    icon: ICONS.shield,
    permission: "view_id_card_print_requests",
    Component: IdCardReprints,
  },
  {
    key: "membership-renewals",
    path: "membership-renewals",
    to: "/admin/membership-renewals",
    end: false,
    label: "Membership Renewals",
    icon: ICONS.refresh,
    permission: "renew_membership",
    // Sidebar-only — folds this and "club-renewals" below under one
    // collapsible "Renewals" parent (see ADMIN_NAV_GROUPS + AdminLayout.jsx).
    // Purely cosmetic: routing/permission gating is unaffected — App.js still
    // maps ADMIN_NAV flatly to routes, and each entry keeps its own
    // independent permission, so a role missing one renewal permission still
    // sees the other as its own link rather than a one-item dropdown.
    group: "renewals",
    Component: MembershipRenewals,
  },
  {
    key: "club-renewals",
    path: "club-renewals",
    to: "/admin/club-renewals",
    end: false,
    label: "Club Renewals",
    icon: ICONS.clubs,
    permission: "renew_club",
    group: "renewals",
    Component: ClubRenewals,
  },
  {
    key: "log-viewer",
    path: "log-viewer",
    to: "/admin/log-viewer",
    end: false,
    label: "Log Viewer",
    // Terminal / list icon — a horizontal-lines motif suggesting log output
    icon: "M3 5h18M3 10h18M3 15h12M3 20h8",
    permission: "view_logs",
    Component: LogViewer,
  },
  {
    // GET /api/system/login-logs is gated on the backend by middleware('role:admin')
    // directly, not a "view_*" permission — so this uses requireSuperAdmin instead
    // of `permission`, same as the requireSuperAdmin doc comment above describes.
    // (A `view_system_user_login_logs` permission does exist in the seeders, but
    // no route or controller ever checks it — see SystemUserLoginLogController.)
    key: "login-logs",
    path: "login-logs",
    to: "/admin/login-logs",
    end: false,
    label: "Login Logs",
    icon: ICONS.clock,
    permission: null,
    requireSuperAdmin: true,
    Component: SystemUserLoginLogs,
  },
];

// Entries tagged with `featureFlag` are dropped entirely while that flag in
// config/featureFlags.js is off — no sidebar link AND no route, so the page
// can't be reached by URL either. Flip the flag back on to restore them.
export const ADMIN_NAV = ALL_ADMIN_NAV.filter((item) => !item.featureFlag || FEATURE_FLAGS[item.featureFlag]);

// Sidebar-only grouping metadata, keyed by the `group` value ADMIN_NAV
// entries above opt into. Entries sharing a group key render as one
// collapsible parent in AdminLayout instead of separate top-level links —
// App.js's route generation and each entry's own permission gate are
// unaffected, so this is safe to extend for future grouped modules.
export const ADMIN_NAV_GROUPS = {
  renewals: { label: "Renewals", icon: ICONS.refresh },
};
